import React, { useState, useEffect } from "react";
import { fornecedores } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { StatusBadge } from "../shared/StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

export function FornecedoresPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    tipo_fornecedor: "nacional",
    razao_social: "",
    nome_fantasia: "",
    cnpj_ou_registro: "",
    pais: "Brasil",
    moeda_padrao: "BRL",
    contato: "",
    email: "",
    telefone: "",
    endereco: "",
    prazo_medio_fornecimento: 0,
    observacoes: "",
  });

  const fetchData = async () => {
    try {
      const response = await fornecedores.list();
      setData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar fornecedores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await fornecedores.update(editingItem.id, formData);
        toast.success("Fornecedor atualizado!");
      } else {
        await fornecedores.create(formData);
        toast.success("Fornecedor criado!");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      tipo_fornecedor: item.tipo_fornecedor || "nacional",
      razao_social: item.razao_social || "",
      nome_fantasia: item.nome_fantasia || "",
      cnpj_ou_registro: item.cnpj_ou_registro || "",
      pais: item.pais || "Brasil",
      moeda_padrao: item.moeda_padrao || "BRL",
      contato: item.contato || "",
      email: item.email || "",
      telefone: item.telefone || "",
      endereco: item.endereco || "",
      prazo_medio_fornecimento: item.prazo_medio_fornecimento || 0,
      observacoes: item.observacoes || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este fornecedor?")) return;
    try {
      await fornecedores.delete(id);
      toast.success("Fornecedor excluído!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      tipo_fornecedor: "nacional",
      razao_social: "",
      nome_fantasia: "",
      cnpj_ou_registro: "",
      pais: "Brasil",
      moeda_padrao: "BRL",
      contato: "",
      email: "",
      telefone: "",
      endereco: "",
      prazo_medio_fornecimento: 0,
      observacoes: "",
    });
  };

  const filteredData = data.filter((item) => {
    const matchesSearch =
      item.razao_social?.toLowerCase().includes(search.toLowerCase()) ||
      item.nome_fantasia?.toLowerCase().includes(search.toLowerCase());
    const matchesTipo = tipoFilter === "all" || item.tipo_fornecedor === tipoFilter;
    return matchesSearch && matchesTipo;
  });

  const columns = [
    { key: "razao_social", header: "Razão Social" },
    { key: "nome_fantasia", header: "Nome Fantasia" },
    {
      key: "tipo_fornecedor",
      header: "Tipo",
      render: (v) => (
        <Badge variant="secondary" className={v === "internacional" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}>
          {v === "internacional" ? "Internacional" : "Nacional"}
        </Badge>
      ),
    },
    { key: "pais", header: "País" },
    { key: "moeda_padrao", header: "Moeda" },
    { key: "prazo_medio_fornecimento", header: "Prazo (dias)" },
    { key: "status", header: "Status", render: (v) => <StatusBadge status={v} /> },
    {
      key: "actions",
      header: "",
      width: "100px",
      render: (_, row) =>
        isAdmin() && (
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={() => handleEdit(row)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => handleDelete(row.id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="fornecedores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Fornecedores</h1>
          <p className="text-muted-foreground">Gerencie seus fornecedores nacionais e internacionais</p>
        </div>
        {isAdmin() && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-fornecedor-btn">
                <Plus className="h-4 w-4 mr-2" />
                Novo Fornecedor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingItem ? "Editar Fornecedor" : "Novo Fornecedor"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo *</Label>
                    <Select value={formData.tipo_fornecedor} onValueChange={(v) => setFormData({ ...formData, tipo_fornecedor: v, pais: v === "nacional" ? "Brasil" : formData.pais, moeda_padrao: v === "nacional" ? "BRL" : formData.moeda_padrao })}>
                      <SelectTrigger data-testid="fornecedor-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nacional">Nacional</SelectItem>
                        <SelectItem value="internacional">Internacional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Razão Social *</Label>
                    <Input
                      value={formData.razao_social}
                      onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                      required
                      data-testid="fornecedor-razao"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome Fantasia</Label>
                    <Input
                      value={formData.nome_fantasia}
                      onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                      data-testid="fornecedor-fantasia"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{formData.tipo_fornecedor === "nacional" ? "CNPJ" : "Registro"}</Label>
                    <Input
                      value={formData.cnpj_ou_registro}
                      onChange={(e) => setFormData({ ...formData, cnpj_ou_registro: e.target.value })}
                      data-testid="fornecedor-cnpj"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>País</Label>
                    <Input
                      value={formData.pais}
                      onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                      data-testid="fornecedor-pais"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Moeda</Label>
                    <Select value={formData.moeda_padrao} onValueChange={(v) => setFormData({ ...formData, moeda_padrao: v })}>
                      <SelectTrigger data-testid="fornecedor-moeda">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BRL">BRL</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="CNY">CNY</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Prazo Médio (dias)</Label>
                    <Input
                      type="number"
                      value={formData.prazo_medio_fornecimento}
                      onChange={(e) => setFormData({ ...formData, prazo_medio_fornecimento: parseInt(e.target.value) || 0 })}
                      data-testid="fornecedor-prazo"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contato</Label>
                    <Input
                      value={formData.contato}
                      onChange={(e) => setFormData({ ...formData, contato: e.target.value })}
                      data-testid="fornecedor-contato"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="fornecedor-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    data-testid="fornecedor-telefone"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-fornecedor-btn">{editingItem ? "Salvar" : "Criar"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-fornecedores"
          />
        </div>
        <Select value={tipoFilter} onValueChange={setTipoFilter}>
          <SelectTrigger className="w-40" data-testid="filter-tipo-fornecedor">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="nacional">Nacional</SelectItem>
            <SelectItem value="internacional">Internacional</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} data={filteredData} loading={loading} emptyMessage="Nenhum fornecedor encontrado" />
    </div>
  );
}
